import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import { Calendar, TrendingUp, Package, Building2, ChevronDown, ChevronUp, BarChart2, Filter } from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

export default function DailyProgressPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('daily_progress');
  const { formatDate, formatCurrency } = useSettings();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0,10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,10));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('by_date'); // by_date | by_product | by_dept
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProject) params.project_id = selectedProject;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/reports/daily-progress', { params });
      setData(res.data);
      setExpandedGroups({});
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Group data by date
  const groupByDate = () => {
    const g = {};
    data.forEach(r => {
      const k = r.work_date?.slice(0,10) || 'Unknown';
      if (!g[k]) g[k] = [];
      g[k].push(r);
    });
    return Object.entries(g).sort((a,b) => b[0].localeCompare(a[0]));
  };

  // Group by product (proto_code)
  const groupByProduct = () => {
    const g = {};
    data.forEach(r => {
      const k = r.proto_code || r.item_name || 'Unknown';
      if (!g[k]) g[k] = [];
      g[k].push(r);
    });
    return Object.entries(g).sort((a,b) => a[0].localeCompare(b[0]));
  };

  // Group by department
  const groupByDept = () => {
    const g = {};
    data.forEach(r => {
      const k = r.dept_name || 'Unknown';
      if (!g[k]) g[k] = [];
      g[k].push(r);
    });
    return Object.entries(g).sort((a,b) => a[0].localeCompare(b[0]));
  };

  const groups = viewMode === 'by_date' ? groupByDate()
    : viewMode === 'by_product' ? groupByProduct()
    : groupByDept();

  const totalQtyDone = data.reduce((s, r) => s + (r.qty_done || 0), 0);
  const uniqueDates = [...new Set(data.map(r => r.work_date?.slice(0,10)))].length;
  const uniqueProducts = [...new Set(data.map(r => r.proto_code))].length;

  const toggleGroup = (k) => setExpandedGroups(p => ({ ...p, [k]: !p[k] }));

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📅 Daily Progress Report</h1>
        <p className="text-gray-500 text-sm mt-1">Date-wise production output — har din kitna kaam hua</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Project</label>
            <ProjectSelect projects={projects} value={selectedProject} onChange={v => setSelectedProject(v)}
              placeholder="All Projects" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
          <button onClick={load} disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 justify-center">
            <Filter className="w-4 h-4" />
            {loading ? 'Loading...' : 'Apply Filter'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{totalQtyDone}</p>
          <p className="text-sm text-gray-500 mt-1">Total Pieces Done</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{uniqueDates}</p>
          <p className="text-sm text-gray-500 mt-1">Working Days</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{uniqueProducts}</p>
          <p className="text-sm text-gray-500 mt-1">Products Active</p>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        {[['by_date','📅 By Date'],['by_product','📦 By Product'],['by_dept','🏭 By Department']].map(([v,l]) => (
          <button key={v} onClick={() => setViewMode(v)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              viewMode === v ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Data */}
      {data.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">Koi data nahi mila — filter change karo ya workers se progress enter karwao</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(([groupKey, rows]) => {
            const groupTotal = rows.reduce((s,r) => s + (r.qty_done||0), 0);
            const isOpen = expandedGroups[groupKey];
            return (
              <div key={groupKey} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <span className="text-base font-bold text-gray-800">
                      {viewMode === 'by_date' ? formatDate(groupKey) : groupKey}
                    </span>
                    <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {groupTotal} pcs
                    </span>
                    <span className="text-xs text-gray-400">{rows.length} entries</span>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500">
                          {viewMode !== 'by_date' && <th className="text-left px-4 py-2">Date</th>}
                          {viewMode !== 'by_product' && <th className="text-left px-4 py-2">Product</th>}
                          {viewMode !== 'by_dept' && <th className="text-left px-4 py-2">Department</th>}
                          <th className="text-left px-4 py-2">Worker</th>
                          <th className="text-center px-4 py-2">Qty Done</th>
                          <th className="text-center px-4 py-2">Task Total</th>
                          <th className="text-center px-4 py-2">Status</th>
                          <th className="text-left px-4 py-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} className="border-t border-gray-50 hover:bg-amber-50/30">
                            {viewMode !== 'by_date' && <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{formatDate(r.work_date)}</td>}
                            {viewMode !== 'by_product' && (
                              <td className="px-4 py-2.5">
                                <p className="font-medium text-gray-800">{r.item_name}</p>
                                {r.proto_code && <p className="text-xs text-gray-400">{r.proto_code}</p>}
                              </td>
                            )}
                            {viewMode !== 'by_dept' && (
                              <td className="px-4 py-2.5">
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                                  style={{ background: r.dept_color || '#6366F1' }}>{r.dept_name}</span>
                              </td>
                            )}
                            <td className="px-4 py-2.5 text-gray-700">{r.worker_name || '—'}</td>
                            <td className="px-4 py-2.5 text-center font-bold text-amber-600">{r.qty_done}</td>
                            <td className="px-4 py-2.5 text-center text-gray-500">
                              {r.quantity_completed}/{r.quantity_assigned}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                r.task_status==='completed' ? 'bg-green-100 text-green-700' :
                                r.task_status==='in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'}`}>
                                {r.task_status}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs max-w-xs truncate">{r.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-amber-50 border-t-2 border-amber-200">
                          <td colSpan={viewMode === 'by_date' ? 2 : 3} className="px-4 py-2 text-sm font-bold text-gray-700">Total</td>
                          <td className="px-4 py-2 text-center font-bold text-amber-700">{groupTotal}</td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
